// i18next setup. English is bundled statically — it is the source language, the
// fallback, and must never flash missing keys. Every other locale's catalog loads
// lazily on first use (loadCatalog), which Vite code-splits into its own chunk.
//
// …except the two big namespaces, `settings` (~70 KB) and `kb` (~29 KB): only views that are
// lazy chunks themselves use them (the Settings modal, the KB manage view, the 2FA enrolment
// gate, the AI-app consent page), so they'd otherwise sit in the render-blocking entry for
// nothing. They load on demand — English and the UI language, like the other catalogs — and
// every view that uses them waits for ensureNamespaces() before it renders (the namespace is
// awaited next to the view's import()), so a raw key never shows. An English catalog that
// fails to load fails the view's load too (lib/lazyPage.js reloads once) rather than rendering keys.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enCommon from './locales/en/common.json';
import enCustomer from './locales/en/customer.json';
import enTicket from './locales/en/ticket.json';
import enAgent from './locales/en/agent.json';
import enSignup from './locales/en/signup.json';
import enSandbox from './locales/en/sandbox.json';

export const NAMESPACES = ['common', 'customer', 'ticket', 'agent', 'settings', 'signup', 'sandbox', 'kb'];
const LAZY_NAMESPACES = ['settings', 'kb'];
const EAGER_NAMESPACES = NAMESPACES.filter((ns) => !LAZY_NAMESPACES.includes(ns));
// Every UI language this document has asked for (English always: the fallback), and the lazy
// namespaces asked for so far. Both only grow, so whichever of loadCatalog / ensureNamespaces
// runs second also covers the first one's (locale, namespace) pairs.
const locales = new Set(['en']);
const wanted = new Set();

i18n.use(initReactI18next).init({
  resources: { en: { common: enCommon, customer: enCustomer, ticket: enTicket, agent: enAgent, signup: enSignup, sandbox: enSandbox } },
  lng: 'en',
  fallbackLng: 'en',
  ns: NAMESPACES,
  defaultNS: 'common',
  interpolation: { escapeValue: false }, // React already escapes
  react: { useSuspense: false }, // catalogs load imperatively; no Suspense boundary
  returnNull: false
});

// One (locale, namespace) catalog, once. A non-English one that fails degrades to English
// (fallbackLng) rather than throwing: a namespace with no file for the locale yet (e.g. a new one
// before its catalogs are generated) rejects in Vite's dynamic-import helper and lands here as an
// empty bundle. English is the fallback itself, so its failure is never swallowed — an empty
// bundle would render raw keys with no way back (e.g. a deploy renamed `settings-<hash>.js` under
// an uncontrolled tab): it rejects, and the view's lazyPage() reloads. A failed load isn't
// remembered here, so a later call asks again (a network blip mid-session) — though an engine
// may keep a failed module fetch for the document's life, so for English the reload is the retry.
const bundles = new Map();
function loadBundle(locale, ns) {
  if (locale === 'en' && EAGER_NAMESPACES.includes(ns)) return Promise.resolve();
  const key = `${locale}/${ns}`;
  if (!bundles.has(key)) {
    bundles.set(key, import(`./locales/${locale}/${ns}.json`)
      .then((m) => m.default, (err) => { bundles.delete(key); if (locale === 'en') throw err; return {}; })
      .then((data) => { i18n.addResourceBundle(locale, ns, data, true, true); }));
  }
  return bundles.get(key);
}

// Lazily add a non-English catalog's namespaces (the eager ones, plus whichever lazy ones a
// view has already asked for), then it's ready for changeLanguage.
export async function loadCatalog(locale) {
  locales.add(locale);
  await Promise.all([...EAGER_NAMESPACES, ...wanted].map((ns) => loadBundle(locale, ns)));
}

// Load lazy namespaces (`['settings', 'kb']`) in English and every UI language asked for. A view
// awaits it next to its own import() — `lazyPage(() => withNamespaces(import(…), […]))`. Rejects
// only when an English catalog failed (above), so a caller that isn't a lazyPage() view must
// catch it and keep its keys hidden; a non-English failure just leaves that language on English.
export async function ensureNamespaces(nss) {
  nss.forEach((ns) => wanted.add(ns));
  await Promise.all(nss.flatMap((ns) => [...locales].map((locale) => loadBundle(locale, ns))));
}
export const withNamespaces = (load, nss) => Promise.all([load, ensureNamespaces(nss)]).then(([mod]) => mod);

export default i18n;
